import { useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, List, ArrowLeft, Shield, Eye, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AbsenceTypes from '@/components/AbsenceTypes';
import DangerZone from '@/components/DangerZone';
import EmployeePortalSettings from '@/components/EmployeePortalSettings';
import SystemLogs from '@/components/SystemLogs';

const Settings = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { path: '/admin/settings/absence-types', icon: List, label: 'Absence Types' },
    { path: '/admin/settings/employee-portal', icon: Eye, label: 'Employee Portal Menu' },
    { path: '/admin/settings/system-logs', icon: FileText, label: 'System Logs' },
  ];

  // Only show Master Settings to tech@reuseabox.co.uk
  if (user?.email === 'tech@reuseabox.co.uk') {
    menuItems.push({ path: '/admin/settings/master', icon: Shield, label: 'Master Settings' });
  }

  return (
    <div className="space-y-6" data-testid="settings-container">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Settings</h2>
            <p className="text-sm text-gray-500">Configure system settings and preferences</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Side Menu */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-md p-4">
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`settings-menu-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                      isActive
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-9">
          <Routes>
            <Route path="absence-types" element={<AbsenceTypes />} />
            <Route path="employee-portal" element={<EmployeePortalSettings />} />
            <Route path="system-logs" element={<SystemLogs />} />
            <Route path="master" element={<DangerZone user={user} />} />
            <Route path="/" element={
              <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">
                <SettingsIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Select a setting from the menu to configure</p>
              </div>
            } />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Settings;
